/**
 * Checks a patient's phone numbers the way the server will, so a number it would refuse is flagged on
 * the field instead of coming back as "Some of this patient's details were rejected".
 *
 * RestPatientValidator (CatTraxRestAPI) reads both numbers with libphonenumber and accepts a valid New
 * Zealand or Australian number on either country's instance: written nationally (021 123 4567,
 * 0412 345 678), with + and the country code, or after an international dialling prefix (00, 0011).
 * The two countries number differently - an NZ mobile is 02 and 7 to 9 more digits, an AU mobile 04
 * and 8 more - so a number is read as each in turn, as the server does. The landline also passes if it
 * matches one of the two regexes that field accepted before libphonenumber.
 *
 * The numbering plans below are libphonenumber 8.13.40's for NZ and AU, the version the REST API ships.
 * If that version is bumped, compare them with the new metadata.
 *
 * A number with any other country code can't be judged without every country's plan, so it is let
 * through and the server decides. This must never refuse a number the server accepts: the form will
 * not submit with a field flagged, so a stricter check here would leave that patient unsaveable.
 */
app.service("PhoneNumber", [function () {

	// The national number lengths a set of numbers allows (none listed: the pattern alone decides) and
	// the pattern the whole national number must match.
	function desc(lengths, pattern) {
		return { lengths: lengths, pattern: new RegExp("^(?:" + pattern + ")$") };
	}

	const NZ = {
		countryCode: "64",
		internationalPrefix: /^0(?:0|161)/,
		nationalPrefix: /^0/,
		general: desc([5, 6, 7, 8, 9, 10], "[1289]\\d{9}|50\\d{5}(?:\\d{2,3})?|[27-9]\\d{7,8}|(?:[34]\\d|6[0-35-9])\\d{6}|8\\d{4,6}"),
		types: [
			desc([8], "24099\\d{3}|(?:3[2-79]|[49][2-9]|6[235-9]|7[2-57-9])\\d{6}"), // landline
			desc([8, 9, 10], "2(?:[0-27-9]\\d|6)\\d{6,7}|2(?:1\\d|75)\\d{5}"), // mobile
			desc([8, 9, 10], "508\\d{6,7}|80\\d{6,8}"), // freephone
			desc([7, 8, 9, 10], "(?:1[13-57-9]\\d{5}|50(?:0[08]|30|66|77|88))\\d{3}|90\\d{6,8}"), // premium rate
			desc([9], "70\\d{7}"), // personal number
			desc([], "8(?:1[16-9]|22|3\\d|4[045]|5[459]|6[235-9]|7[0-3579]|90)\\d{2,7}"), // universal access
		],
	};

	// Shared by Australia and the two territories on +61.
	const AU_MOBILE = desc([9], "4(?:79[01]|83[0-389]|94[0-4])\\d{5}|4(?:[0-36]\\d|4[047-9]|5[0-25-9]|7[02-8]|8[0-24-9]|9[0-37-9])\\d{6}");
	const AU_FREEPHONE = desc([7, 10], "180(?:0\\d{3}|2)\\d{3}");
	const AU_PREMIUM = desc([10], "190[0-26]\\d{6}");
	const AU_SHARED_COST = desc([6, 8, 10, 12], "13(?:00\\d{6}(?:\\d{2})?|45[0-4]\\d{3})|13\\d{4}");
	const AU_VOIP = desc([9], "14(?:5(?:1[0458]|[23][458])|71\\d)\\d{4}");

	const AU = {
		countryCode: "61",
		internationalPrefix: /^(?:001[14-689]|14(?:1[14]|34|4[17]|[56]6|7[47]|88)0011)/,
		// 1831 and 1832 are carrier selection codes, dialled ahead of the number.
		nationalPrefix: /^(?:183[12]|0)/,
		general: desc([5, 6, 7, 8, 9, 10, 12], "1(?:[0-79]\\d{7}(?:\\d(?:\\d{2})?)?|8[0-24-9]\\d{7})|[2-478]\\d{8}|1\\d{4,7}"),
		types: [
			desc([9], "(?:(?:(?:2(?:[0-26-9]\\d|3[0-8]|4[02-9]|5[0135-9])|7(?:[013-57-9]\\d|2[0-8]))\\d|3(?:(?:[0-3589]\\d|6[1-9]|7[0-35-9])\\d|4(?:[0-578]\\d|90)))\\d\\d|8(?:51(?:0(?:0[03-9]|[12479]\\d|3[2-9]|5[0-8]|6[1-9]|8[0-7])|1(?:[0235689]\\d|1[0-69]|4[0-589]|7[0-47-9])|2(?:0[0-79]|[18][13579]|2[14-9]|3[0-46-9]|[4-6]\\d|7[89]|9[0-4])|3\\d\\d)|(?:6[0-8]|[78]\\d)\\d{3}|9(?:[02-9]\\d{3}|1(?:(?:[0-58]\\d|6[0135-9])\\d|7(?:0[0-24-9]|[1-9]\\d)|9(?:[0-46-9]\\d|5[0-79])))))\\d{3}"), // landline
			AU_MOBILE,
			AU_FREEPHONE,
			AU_PREMIUM,
			AU_SHARED_COST,
			AU_VOIP,
			desc([5, 6, 7, 8, 9], "163\\d{2,6}"), // pager
		],
	};

	// Christmas Island and the Cocos Islands share +61 but number their landlines apart from Australia.
	const TERRITORY_GENERAL = desc([6, 7, 8, 9, 10, 12], "1(?:[0-79]\\d{8}(?:\\d{2})?|8[0-24-9]\\d{7})|[148]\\d{8}|1\\d{5,7}");
	const COCOS_ISLANDS = {
		general: TERRITORY_GENERAL,
		types: [
			desc([9], "8(?:51(?:0(?:02|31|60|89)|1(?:18|76)|223)|91(?:0(?:1[0-2]|29)|1(?:[28]2|50|79)|2(?:10|64)|3(?:[06]8|22)|4[29]8|62\\d|70[23]|959))\\d{3}"),
			AU_MOBILE, AU_FREEPHONE, AU_PREMIUM, AU_SHARED_COST, AU_VOIP,
		],
	};
	const CHRISTMAS_ISLAND = {
		general: TERRITORY_GENERAL,
		types: [
			desc([9], "8(?:51(?:0(?:01|30|59|88)|1(?:17|46|75)|2(?:22|35))|91(?:00[6-9]|1(?:[28]1|49|78)|2(?:09|63)|3(?:12|26|75)|4(?:56|97)|64\\d|7(?:0[01]|1[0-2])|958))\\d{3}"),
			AU_MOBILE, AU_FREEPHONE, AU_PREMIUM, AU_SHARED_COST, AU_VOIP,
		],
	};

	// The countries a bare national number is read as, as the server's PhoneNumberUtil.nationalFormatRegions.
	const REGIONS = [NZ, AU];
	// Reading a number with a country code uses that country's own prefixes and lengths...
	const COUNTRY = { 64: NZ, 61: AU };
	// ...and it is valid if it fits any plan on that code.
	const PLANS = { 64: [NZ], 61: [AU, COCOS_ISLANDS, CHRISTMAS_ISLAND] };

	// Some other country's number - not judged here.
	const ELSEWHERE = {};

	// The landline field's regexes from before libphonenumber, matched against the number as typed.
	const LEGACY_LANDLINE = [
		/^0(([3,4,6,7,9])(\d{3})(\d{4})|(800\d{5,8})|(508\d{5,8})|([2][0-9]{1})(\d{6,8}))$/,
		/^(0)[23478](\s)?\d{4}(\s)?\d{4}$/,
	];

	function fits(d, number) {
		return (d.lengths.length === 0 || d.lengths.indexOf(number.length) > -1) && d.pattern.test(number);
	}

	function isValidOn(countryCode, number) {
		return PLANS[countryCode].some(function (plan) {
			return fits(plan.general, number) && plan.types.some(function (type) {
				return fits(type, number);
			});
		});
	}

	function lengthCheck(plan, number) {
		const lengths = plan.general.lengths;
		if (number.length < lengths[0]) {
			return "TOO_SHORT";
		}
		if (number.length > lengths[lengths.length - 1]) {
			return "TOO_LONG";
		}
		return lengths.indexOf(number.length) > -1 ? "POSSIBLE" : "INVALID_LENGTH";
	}

	// Takes off the national prefix (0 - or 1831/1832 in AU), unless the number fits the plan only with it.
	function withoutNationalPrefix(plan, number) {
		const prefix = plan.nationalPrefix.exec(number);
		if (!prefix) {
			return number;
		}
		const rest = number.slice(prefix[0].length);
		return plan.general.pattern.test(number) && !plan.general.pattern.test(rest) ? number : rest;
	}

	/**
	 * What the server's parse makes of the digits against one region: { countryCode, number } with the
	 * national number, ELSEWHERE for another country's code, or null when it can't be read.
	 */
	function read(region, digits, hasPlus) {
		let international = hasPlus;
		let rest = digits;

		if (!hasPlus) {
			const idd = region.internationalPrefix.exec(digits);
			// Not when a 0 follows: no country code starts with one.
			if (idd && digits.charAt(idd[0].length) !== "0") {
				rest = digits.slice(idd[0].length);
				international = true;
			}
		}

		let countryCode = null;
		let national = rest;

		if (international) {
			if (rest.length <= 2) {
				return null;
			}
			if (rest.charAt(0) === "0") {
				// No such country code. After a + the server tries once more without it, and keeps the result
				// only if the rest carries a country code of its own (an 00 or 0011 prefix).
				const again = hasPlus ? read(region, rest, false) : null;
				return again === ELSEWHERE || (again && again.carriedCode) ? again : null;
			}
			countryCode = rest.slice(0, 2);
			if (!COUNTRY[countryCode]) {
				return ELSEWHERE;
			}
			national = rest.slice(2);
		} else {
			// The country's own code typed without the +, e.g. 64 21 123 4567. Taken as one when the number
			// only fits the plan without it, or is too long to be a national number.
			if (digits.indexOf(region.countryCode) === 0) {
				const potential = withoutNationalPrefix(region, digits.slice(region.countryCode.length));
				if ((!region.general.pattern.test(digits) && region.general.pattern.test(potential)) || lengthCheck(region, digits) === "TOO_LONG") {
					countryCode = region.countryCode;
					national = potential;
				}
			}
			if (!countryCode) {
				return finish(region.countryCode, digits, false);
			}
		}
		return finish(countryCode, national, true);
	}

	function finish(countryCode, national, carriedCode) {
		if (national.length < 2) {
			return null;
		}
		const plan = COUNTRY[countryCode];
		const stripped = withoutNationalPrefix(plan, national);
		const length = lengthCheck(plan, stripped);
		if (length !== "TOO_SHORT" && length !== "INVALID_LENGTH") {
			national = stripped;
		}
		if (national.length < 2 || national.length > 17) {
			return null;
		}
		return { countryCode: countryCode, number: national, carriedCode: carriedCode };
	}

	var service = {

		/**
		 * Whether the server will accept this as a patient's mobile number. It only asks for a valid number,
		 * mobile or not - see RestPatientValidator.
		 */
		isValidMobile: function (value) {
			if (value === null || value === undefined) {
				return false;
			}
			// libphonenumber skips anything before the first + or digit, and trailing punctuation.
			const text = String(value).replace(/^[^+\d]+/, "").replace(/[^\dA-Za-z#]+$/, "");
			// Only digits and the usual separators, + only at the start. The server would take some
			// stranger input (an extension, a letter for a digit) that the field has never accepted.
			if (!/^\+*[\d ().\/-]*$/.test(text)) {
				return false;
			}
			const digits = text.replace(/\D/g, "");
			if (digits.length < 3) {
				return false;
			}
			const hasPlus = text.charAt(0) === "+";
			return REGIONS.some(function (region) {
				const parsed = read(region, digits, hasPlus);
				return parsed === ELSEWHERE || (parsed !== null && isValidOn(parsed.countryCode, parsed.number));
			});
		},

		/**
		 * Whether the server will accept this as a patient's landline: a valid number, or one the field's
		 * older regexes accepted, so that nothing already on file stops saving.
		 */
		isValidLandline: function (value) {
			if (value === null || value === undefined) {
				return false;
			}
			return LEGACY_LANDLINE.some(function (pattern) {
				return pattern.test(String(value));
			}) || service.isValidMobile(value);
		},
	};

	return service;
}]);
